import React, { useEffect, useState } from "react";
import {
  Table,
  Tag,
  Button,
  Row,
  Col,
  Card,
  Spin,
  Tooltip,
  Modal,
  Input,
  message,
  Checkbox,
  Space,
  Drawer,
  Upload,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  GlobalOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { FaEye, FaPen, FaTrashAlt } from "react-icons/fa";
import { hasPermission } from "../../utils/HasPermission";
import Addcontactmodal from "./Add";
import Editcontactmodal from "./Edit";
import {
  getAllUserAttendanceExport,
  getContacts,
  deleteContacts,
  getexportContacts,
  importContacts,
  pinContact,
  getAllSources,
  getAllTags,
} from "../../utils/API";
import { RiAddBoxFill } from "react-icons/ri";
import * as XLSX from "xlsx";
import { EyeOutlined, StarFilled, StarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  FacebookFilled,
  TwitterOutlined,
  InstagramOutlined,
  GithubOutlined,
  YoutubeOutlined,
} from "@ant-design/icons";
import {
  SortOrder,
  ColumnGroupType,
  ColumnType,
} from "antd/es/table/interface";
import { RiUnpinLine } from "react-icons/ri";
import { TiPinOutline } from "react-icons/ti";
import { autoRegisterCurrentPageColumns } from "../../utils/emailTemplateColumns";

interface Contact {
  companyName: any;
  isPinned?: boolean;
  _id: string;
  imageUrl: string;
  contactName: string;
  email: string;
  phone: string;
  leadStatus: string;
  company: string;
  leadSource: string;
  tags: string[];
  avatarUrl?: string;
  createdAt: string;
  updatedAt: string;
  score?: number;
  state?: string;
  deals?: number;
  contributions?: number;
  committed?: number;
  bio?: string;
  address?: string;
  city?: string;
  country?: string;
  position?: string;
  socialLinks?: {
    facebook?: string;
    twitter?: string;
    instagram?: string;
    github?: string;
    youtube?: string;
  };
  profilePhoto?: string;
}

interface Source {
  _id: string;
  name: string;
  slug?: string;
  category?: string[];
}

interface Tag {
  _id: string;
  name: string;
  color?: string;
  category?: string[];
}

type TagType =
  | "Customer"
  | "HotLead"
  | "Influencer"
  | "LostCustomer"
  | "NewLead"
  | "Partner"
  | "Prospect"
  | "Subscriber";

const tagsStyles: Record<TagType, { background: string; text: string }> = {
  Customer: {
    background: "#dcfce7",
    text: "#15803d",
  },
  HotLead: {
    background: "#fee2e2",
    text: "#b91c1c",
  },
  Influencer: {
    background: "#dbeafe",
    text: "#1d4ed8",
  },
  LostCustomer: {
    background: "#fef9c3",
    text: "#ca8a04",
  },
  NewLead: {
    background: "#ede9fe",
    text: "#7c3aed",
  },
  Partner: {
    background: "#fce7f3",
    text: "#db2777",
  },
  Prospect: {
    background: "#ffedd5",
    text: "#ea580c",
  },
  Subscriber: {
    background: "#dcfce7",
    text: "#15803d",
  },
};

const ContactTable = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<Contact[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  console.log("Total Records:", totalRecords);
  const [page, setPage] = useState<number>(1);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [searchText, setSearchText] = useState<string>("");
  const [exportLoading, setExportLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [currentLead, setCurrentLead] = useState<Contact | null>(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [viewLead, setViewLead] = useState<Contact | null>(null);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [downloadTemplateLoading, setDownloadTemplateLoading] = useState(false);
  const [imageErrorMap, setImageErrorMap] = useState<Record<string, boolean>>(
    {},
  );
  const limit = 10;
  const [imageLoadError, setImageLoadError] = useState(false);
  const [sources, setSources] = useState<Source[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [tagsLoading, setTagsLoading] = useState(false);

  // Create lookup objects
  const sourceLookup = sources.reduce((acc: Record<string, string>, source) => {
    acc[source._id] = source.name;
    return acc;
  }, {});

  const tagLookup = tags.reduce(
    (acc: Record<string, { name: string; color?: string }>, tag) => {
      acc[tag._id] = { name: tag.name, color: tag.color };
      return acc;
    },
    {},
  );

  // Fetch all sources
  const fetchAllSources = async () => {
    try {
      setSourcesLoading(true);
      const response = await getAllSources("", 1);
      // Filter sources that have "Contacts" in category if needed
      const filteredSources = Array.isArray(response.sources)
        ? response.sources.filter(
            (source: Source) =>
              !source.category ||
              source.category.includes("Contacts") ||
              source.category.length === 0,
          )
        : response.sources || [];
      setSources(filteredSources);
    } catch (error) {
      console.error("Error fetching sources:", error);
      message.error("Failed to load sources");
    } finally {
      setSourcesLoading(false);
    }
  };

  // Fetch all tags
  const fetchAllTags = async () => {
    try {
      setTagsLoading(true);
      const response = await getAllTags();
      // Filter tags that have "Contacts" in category if needed
      const filteredTags = Array.isArray(response.tags)
        ? response.tags.filter(
            (tag: Tag) =>
              !tag.category ||
              tag.category.includes("Contacts") ||
              tag.category.length === 0,
          )
        : response.tags || [];
      setTags(filteredTags);
    } catch (error) {
      console.error("Error fetching tags:", error);
      message.error("Failed to load tags");
    } finally {
      setTagsLoading(false);
    }
  };

  // Fetch both sources and tags on component mount
  useEffect(() => {
    fetchAllSources();
    fetchAllTags();
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    return `${formattedDate} - ${formattedTime}`;
  };

  const fetchContacts = async (params: { page: number; search?: string }) => {
    setLoading(true);
    try {
      const response = await getContacts({
        page: params.page,
        limit: limit,
        search: params.search || "",
      });

      let contactsData: Contact[] = [];
      let totalCount: number = 0;

      if (
        Array.isArray(response.data.data) &&
        response.data.totalCount !== undefined
      ) {
        contactsData = response.data.data;
        totalCount = response.data.totalCount;
      } else if (Array.isArray(response.data)) {
        contactsData = response.data;
        totalCount = response.data.length;
      } else {
        contactsData = response.data.items || [];
        totalCount = response.data.total || 0;
      }

      console.log("Fetched:", contactsData.length, "Total:", totalCount);

      setData(contactsData);
      console.log("Contacts Data:", response.total);
      setTotalRecords(response.total);
    } catch (error) {
      console.error("Error fetching contacts:", error);
      message.error("Failed to load contacts");
      setData([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts({ page, search: searchText });
  }, [page, searchText]);

  const handleUpdate = async (values: any) => {
    if (!currentLead) return;

    try {
      message.success("Contact updated successfully");
      setIsEditModalVisible(false);
      fetchContacts({ page, search: searchText });
    } catch (error) {
      message.error("Failed to update contact");
    }
  };

  const handleCreate = async (values: any) => {
    try {
      message.success("Contact created successfully");
      setIsAddModalVisible(false);
      fetchContacts({ page: 1, search: searchText });
    } catch (error) {
      message.error("Failed to create contact");
    }
  };

  const handleEdit = (record: Contact) => {
    setCurrentLead(record);
    setIsEditModalVisible(true);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setPage(1);
  };

  const handleAddLead = () => {
    setIsAddModalVisible(true);
  };

  const handleDeleteConfirmation = (id: string) => {
    setDeleteId(id);
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await deleteContacts(deleteId);
      message.success("Contact deleted successfully");

      fetchContacts({ page, search: searchText });

      if (data.length === 1 && page > 1) {
        setPage(page - 1);
      }
    } catch (error) {
      message.error("Failed to delete contact");
      console.error("Delete error:", error);
    } finally {
      setDeleteId(null);
    }
  };

  const handleView = (record: Contact) => {
    setViewLead(record);
    setIsViewModalVisible(true);
  };

  const exportToExcel = async () => {
    setExportLoading(true);
    try {
      const response = await getexportContacts({ search: "" });

      let exportData: Contact[] = [];

      if (Array.isArray(response.data.data)) {
        exportData = response.data.data;
      } else if (Array.isArray(response.data)) {
        exportData = response.data;
      } else if (Array.isArray(response.data.items)) {
        exportData = response.data.items;
      }

      if (!exportData || exportData.length === 0) {
        message.warning("No contacts data to export");
        return;
      }

      const mappedData = exportData.map((item: Contact, index: number) => ({
        "#": index + 1,
        "Contact Name": item.contactName || "-",
        Time: dayjs(item.createdAt).format("DD-MM-YYYY HH:mm") || "-",
        "Lead Score": item.score || "-",
        Email: item.email || "-",
        Phone: item.phone || "-",
        "Company Name": item.company || "-",
        "Lead Source": sourceLookup[item.leadSource] || item.leadSource || "-",
        Tags:
          item.tags
            ?.map((tagId) => tagLookup[tagId]?.name || tagId)
            .join(", ") || "-",
      }));

      const ws = XLSX.utils.json_to_sheet(mappedData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Contacts");

      const currentDate = dayjs().format("DD-MM-YYYY");
      const fileName = `Contacts_Export_${currentDate}.xlsx`;

      XLSX.writeFile(wb, fileName);
      message.success(`Exported ${mappedData.length} contacts`);
    } catch (error) {
      console.error("Export error:", error);
      message.error("Failed to export contacts data");
    } finally {
      setExportLoading(false);
    }
  };

  // Download template for import
  const downloadTemplate = () => {
    setDownloadTemplateLoading(true);
    try {
      const templateData = [
        {
          contactName: "John Doe",
          score: "50",
          email: "john@example.com",
          phone: "+1234567890",
          company: "Example Corp",
          leadSource: "Social Media",
          tags: "Customer,HotLead",
          LastContacted: "2024-01-15 10:30:00",
        },
        {
          contactName: "Jane Smith",
          score: "75",
          phone: "+0987654321", // Only phone, no email (valid)
          company: "Tech Solutions",
          leadSource: "Website",
          tags: "Prospect",
          LastContacted: "2024-01-16 14:45:00",
        },
      ];

      const ws = XLSX.utils.json_to_sheet(templateData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Template");

      // Add instructions
      const instructions = [
        ["⚠️ IMPORTANT INSTRUCTIONS"],
        [
          "1. All fields except 'contactName' are optional, but either 'email' or 'phone' must be provided",
        ],
        ["2. 'contactName' field is required"],
        ["3. 'score' should be a number (default is 0 if not provided)"],
        [
          "4. 'tags' should be comma-separated values (e.g., 'Customer,HotLead')",
        ],
        ["5. 'LastContacted' format: YYYY-MM-DD HH:mm:ss"],
        ["6. Do not change column headers"],
        ["7. Remove example rows before uploading your data"],
        ["", "", "", "", "", "", ""], // Empty row
      ];

      XLSX.utils.sheet_add_aoa(ws, instructions, { origin: -1 });

      XLSX.writeFile(wb, "Contacts_Import_Template.xlsx");
      message.success("Template downloaded successfully");
    } catch (error) {
      console.error("Template download error:", error);
      message.error("Failed to download template");
    } finally {
      setDownloadTemplateLoading(false);
    }
  };

  // Handle file import
  const handleImport = async (file: File) => {
    setImportLoading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await importContacts(formData);

      if (response.success) {
        message.success(response.message);
        setImportModalVisible(false);
        fetchContacts({ page: 1, search: searchText });
      } else {
        message.error(response.data.message || "Import failed");
      }
    } catch (error: any) {
      console.error("Import error:", error);

      // Handle validation errors from backend
      if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.join("\n");
        message.error(
          <div style={{ maxHeight: "200px", overflowY: "auto" }}>
            {error.response.data.message}
            <pre style={{ whiteSpace: "pre-wrap", marginTop: "10px" }}>
              {errorMessages}
            </pre>
          </div>,
        );
      } else {
        message.error(
          error.response?.data?.message || "Failed to import contacts",
        );
      }
    } finally {
      setImportLoading(false);
    }
  };

  const handlePin = async (record: Contact) => {
    try {
      const res = await pinContact(record._id);

      if (res.success) {
        message.success(res.message);
        fetchContacts({ page, search: searchText }); // refresh list
      }
    } catch (err) {
      message.error("Failed to update pin");
    }
  };

  // Upload props for Ant Design Upload component
  const uploadProps = {
    name: "file",
    accept: ".xlsx,.xls",
    showUploadList: false,
    beforeUpload: (file: File) => {
      const isExcel =
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel";

      if (!isExcel) {
        message.error("You can only upload Excel files!");
        return false;
      }

      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error("File must be smaller than 5MB!");
        return false;
      }

      handleImport(file);
      return false; // Prevent default upload
    },
  };

  const onCloseDrawer = () => {
    setIsViewModalVisible(false);
  };

  const getInitials = (name?: string) => {
    if (!name) return "";
    const words = name.trim().split(/\s+/);
    return (
      (words[0]?.[0] || "").toUpperCase() + (words[1]?.[0] || "").toUpperCase()
    );
  };

  const columns: (ColumnGroupType<Contact> | ColumnType<Contact>)[] = [
    {
      title: "",
      key: "selection",
      width: 50,
      fixed: "left" as const,
      render: (_: any, record: Contact) => (
        <Checkbox
          checked={selectedRows.includes(record._id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRows([...selectedRows, record._id]);
            } else {
              setSelectedRows(selectedRows.filter((id) => id !== record._id));
            }
          }}
        />
      ),
    },
    {
      title: "",
      key: "pin",
      width: 50,
      fixed: "left",
      render: (_: any, record: Contact) => (
        <Tooltip title={record.isPinned ? "Unpin" : "Pin"}>
          <span onClick={() => handlePin(record)} style={{ cursor: "pointer" }}>
            {record.isPinned ? (
              <RiUnpinLine style={{ color: "#1677ff", fontSize: 18 }} />
            ) : (
              <TiPinOutline style={{ color: "#999", fontSize: 18 }} />
            )}
          </span>
        </Tooltip>
      ),
    },

    {
      title: "Contact Name",
      dataIndex: "contactName",
      key: "contactName",
      fixed: "left" as const,
      render: (_: string, record: Contact) => {
        const imageUrl = record.profilePhoto
          ? `${process.env.REACT_APP_IMAGE_URL}/contacts/${record.profilePhoto}`
          : null;

        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div
              style={{
                width: 35,
                height: 35,
                borderRadius: "50%",
                backgroundColor: "#e5e7eb",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                flexShrink: 0,
                fontWeight: 600,
                fontSize: 14,
                color: "#374151",
                textTransform: "uppercase",
              }}
            >
              {record.profilePhoto && !imageErrorMap[record._id] ? (
                <img
                  src={`${process.env.REACT_APP_IMAGE_URL}/contacts/${record.profilePhoto}`}
                  alt={record.contactName}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                  onError={() =>
                    setImageErrorMap((prev) => ({
                      ...prev,
                      [record._id]: true,
                    }))
                  }
                />
              ) : (
                <span>{getInitials(record.contactName)}</span>
              )}
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                cursor: "pointer",
              }}
            >
              <span>{record.contactName}</span>
              <Tooltip
                title="Last Contacted"
                color="#CCCCCC"
                overlayInnerStyle={{ color: "#000", padding: "12px" }}
              >
                <span className="text-muted text-xs">
                  {formatDate(record.updatedAt)}
                </span>
              </Tooltip>
            </div>
          </div>
        );
      },
    },

    {
      title: "Lead Score",
      dataIndex: "score",
      key: "score",
      fixed: "left" as const,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "Company",
      dataIndex: "company",
      key: "company",
    },
    {
      title: "Lead Source",
      dataIndex: "leadSource",
      key: "leadSource",
      render: (leadSourceId: string) => {
        if (!leadSourceId) return "-";
        return sourceLookup[leadSourceId] || leadSourceId;
      },
    },
    {
      title: "Tags",
      dataIndex: "tags",
      key: "tags",
      width: 150,
      render: (tagIds: string[]) => {
        if (!tagIds || tagIds.length === 0) return "-";

        return (
          <Space size={[4, 4]} wrap>
            {tagIds?.map((tagId, index) => {
              const tagInfo = tagLookup[tagId];
              const tagName = tagInfo?.name || tagId;
              const tagColor = tagInfo?.color || "#f0f0f0";

              const matchedStyleKey = Object.keys(tagsStyles).find((key) =>
                tagName.toLowerCase().includes(key.toLowerCase()),
              ) as TagType | undefined;

              const style = matchedStyleKey
                ? tagsStyles[matchedStyleKey]
                : {
                    background: tagColor,
                    text: "#000",
                  };

              return (
                <span
                  key={index}
                  className="badge"
                  style={{
                    background: style.background,
                    color: style.text,
                    fontSize: "10px",
                    padding: "4px 10px",
                    whiteSpace: "nowrap",
                    textAlign: "center",
                    borderRadius: "4px",
                    fontWeight: 700,
                    display: "inline-block",
                  }}
                >
                  {tagName}
                </span>
              );
            })}
          </Space>
        );
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: Contact) => (
        <div className="d-flex" style={{ gap: "8px", alignItems: "center" }}>
          {hasPermission("customers", "get") && (
            <Tooltip title="View">
              <button
                onClick={() => handleView(record)}
                className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-cyan-600 
                shadow-[0_4px_12px_rgba(59,130,246,0.3)] 
                hover:shadow-[0_6px_16px_rgba(59,130,246,0.4)] 
                active:translate-y-[1px] active:shadow-none 
                text-white flex items-center justify-center 
                transition-all duration-200 ease-in-out group"
              >
                <EyeOutlined
                  className="group-hover:scale-110 transition-transform duration-200"
                  style={{ fontSize: "12px" }}
                />
              </button>
            </Tooltip>
          )}
          {hasPermission("customers", "update") && (
            <Tooltip title="Edit">
              <button
                onClick={() => handleEdit(record)}
                className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 
                shadow-[0_4px_12px_rgba(139,92,246,0.3)] 
                hover:shadow-[0_6px_16px_rgba(139,92,246,0.4)] 
                active:translate-y-[1px] active:shadow-none 
                text-white flex items-center justify-center 
                transition-all duration-200 ease-in-out group"
              >
                <FaPen
                  className="group-hover:scale-110 transition-transform duration-200"
                  size={12}
                />
              </button>
            </Tooltip>
          )}
          {hasPermission("customers", "delete") && (
            <Tooltip title="Delete">
              <button
                onClick={() => handleDeleteConfirmation(record._id)}
                className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-500 to-rose-700 
                shadow-[0_4px_12px_rgba(244,63,94,0.3)] 
                hover:shadow-[0_6px_16px_rgba(244,63,94,0.4)] 
                active:translate-y-1 active:shadow-none 
                text-white flex items-center justify-center 
                transition-all duration-200 group"
              >
                <FaTrashAlt
                  className="group-hover:scale-110 transition-transform"
                  size={12}
                />
              </button>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  useEffect(() => {
    autoRegisterCurrentPageColumns(columns);
  }, []);

  return (
    <>
      <Row className="m-2" gutter={[16, 16]} align="middle">
        <Col xs={24} sm={24} md={6} lg={6} xl={6}>
          <h2 style={{ fontSize: "20px", fontWeight: 600, margin: 0 }}>
            Contacts
          </h2>
        </Col>

        <Col xs={24} sm={24} md={18} lg={18} xl={18}>
          <div
            style={{
              display: "flex",
              gap: 16,
              justifyContent: "flex-end",
              height: "33px",
            }}
          >
            <Input
              placeholder="Search..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={() => handleSearch(searchText)}
              allowClear
              style={{ width: 200 }}
            />

            <Button
              type="primary"
              onClick={handleAddLead}
              style={{ background: "#001529", height: "33px" }}
            >
              <RiAddBoxFill className="button-icon" />
              <span className="button-text">Add</span>
            </Button>

            <Upload {...uploadProps}>
              <Button
                loading={importLoading}
                style={{
                  background: "#001529",
                  color: "white",
                  border: "none",
                  height: "33px",
                  minWidth: "100px",
                }}
              >
                {/* <UploadOutlined />  */}
                Import Data
              </Button>
            </Upload>

            {/* <Button
              onClick={downloadTemplate}
              loading={downloadTemplateLoading}
              style={{
                background: "#6c757d",
                color: "white",
                border: "none",
                height: "33px",
                minWidth: "140px",
              }}
            >
              Download Template
            </Button> */}

            <Button
              onClick={exportToExcel}
              style={{
                background: "#001529",
                color: "white",
                border: "none",
                height: "33px",
                minWidth: "100px",
              }}
              disabled={exportLoading}
            >
              {exportLoading ? "Exporting..." : "Export Data"}
            </Button>
          </div>
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <Card className="w-100">
            <Spin spinning={loading}>
              <div className="scrollbar-hide custom-scroll-table">
                <Table
                  columns={columns}
                  dataSource={data}
                  rowKey={(record) => record._id}
                  scroll={{ x: "max-content" }}
                  loading={loading}
                  pagination={{
                    pageSize: limit,
                    current: page,
                    total: totalRecords,
                    showSizeChanger: false,
                    onChange: (newPage) => {
                      setPage(newPage); // ✅ this is enough
                    },
                  }}
                />
              </div>
            </Spin>
          </Card>
        </Col>
      </Row>

      {/* Delete Confirmation Modal */}
      <Modal
        open={!!deleteId}
        onOk={handleDelete}
        onCancel={() => setDeleteId(null)}
        okText="Yes"
        cancelText="No"
        width={500}
        centered={false}
        destroyOnClose
        className="custom-modal"
        style={{
          bottom: 20,
          top: "10vw",
          maxHeight: "calc(100vh - 30px)",
          margin: "0 auto",
          position: "relative",
        }}
        title={
          <div style={{ marginLeft: "11px", width: "100%", paddingRight: 20 }}>
            Confirmation
          </div>
        }
        okButtonProps={{
          style: {
            backgroundColor: "#ff4d4f",
            borderColor: "#ff4d4f",
            color: "white",
          },
        }}
        bodyStyle={{
          padding: "22px",
          overflowY: "auto",
          fontFamily: "inter, sans-serif",
        }}
        styles={{
          header: {
            borderBottom: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
          content: { padding: 0, overflow: "hidden" },
          footer: {
            borderTop: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
        }}
      >
        <p>Are you sure you want to delete this Contacts?</p>
      </Modal>

      {/* View Drawer */}
      <Drawer
        placement="right"
        onClose={onCloseDrawer}
        open={isViewModalVisible}
        width={380}
        bodyStyle={{ padding: 0, backgroundColor: "#fff" }}
        headerStyle={{ display: "none" }}
      >
        {viewLead && (
          <div className="text-sm">
            <div
              style={{ backgroundColor: "#001529" }}
              className="text-white p-4 relative"
            >
              <button
                onClick={onCloseDrawer}
                className="absolute top-2 right-2 text-white text-lg font-bold hover:text-gray-300"
              >
                &times;
              </button>

              <div className="flex items-center gap-4">
                {imageLoadError || !viewLead.imageUrl ? (
                  <div className="w-16 h-16 rounded-full border-2 border-white bg-gray-300 flex items-center justify-center text-lg font-bold">
                    {getInitials(viewLead.contactName || "")}
                  </div>
                ) : (
                  <img
                    src={viewLead.imageUrl}
                    alt="Profile"
                    className="w-16 h-16 rounded-full object-cover border-2 border-white"
                    onError={() => setImageLoadError(true)}
                  />
                )}
                <div>
                  <h2 className="text-lg font-semibold">
                    {viewLead.contactName}
                  </h2>
                  <p className="text-sm">
                    {viewLead.position || "Designation"}
                  </p>

                  <div className="flex items-center gap-2 text-xs text-gray-200 mt-1">
                    <span>
                      {viewLead.updatedAt
                        ? formatDate(viewLead.updatedAt)
                        : "N/A"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-200 mt-1">
                    <EnvironmentOutlined className="text-base" />
                    <span>{viewLead.state || "-"}</span>
                    <span className="mx-1"></span>
                    <GlobalOutlined className="text-base" />
                    <span>{viewLead.country || "-"}</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 text-center mt-4">
                <div>
                  <p className="font-bold text-base">{viewLead.deals || 0}</p>
                  <p className="text-xs">Deals</p>
                </div>
                <div>
                  <p className="font-bold text-base">
                    ${viewLead.contributions || 0}
                  </p>
                  <p className="text-xs">Contributions</p>
                </div>
                <div>
                  <p className="font-bold text-base">
                    ${viewLead.committed || 0}
                  </p>
                  <p className="text-xs">Committed</p>
                </div>
              </div>
            </div>

            <div className="bg-white text-black">
              <div className="p-4">
                <p className="font-semibold text-[#001529] mb-2">
                  Professional Bio:
                </p>
                <p className="text-gray-700">
                  {viewLead.bio ||
                    `I am ${viewLead.contactName || "a professional"}, working in my field.`}
                </p>
              </div>
              <hr
                className="border-t border-dotted p-3 "
                style={{ borderColor: "gray-200" }}
              />

              {/* Add Lead Source to View Drawer */}
              <div className="px-4 pb-4">
                <p className="font-semibold text-[#001529] mb-2">
                  Lead Source:
                </p>
                <p className="text-gray-700">
                  {viewLead.leadSource
                    ? sourceLookup[viewLead.leadSource] || viewLead.leadSource
                    : "N/A"}
                </p>
              </div>
              <hr
                className="border-t border-dotted p-3 "
                style={{ borderColor: "gray-200" }}
              />

              <div className="px-4 pb-4">
                <p className="font-semibold text-[#001529] mb-2">
                  Contact Information:
                </p>
                <ul className="text-gray-700 space-y-3">
                  <li className="flex items-center gap-2">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 text-blue-600">
                      <MailOutlined />
                    </div>
                    <span className="text-sm">{viewLead.email || "N/A"}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 text-green-600">
                      <PhoneOutlined />
                    </div>
                    <span className="text-sm">{viewLead.phone || "N/A"}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 text-red-600">
                      <EnvironmentOutlined />
                    </div>
                    <span className="text-sm">
                      {viewLead.address
                        ? `${viewLead.address}, ${viewLead.city || ""}, ${viewLead.state || ""}, ${viewLead.country || ""}`
                        : "N/A"}
                    </span>
                  </li>
                </ul>
              </div>
              <hr
                className="border-t border-dotted p-3 "
                style={{ borderColor: "gray-200" }}
              />

              <div className="flex items-center gap-4 px-4 pb-4 ">
                <span className="font-semibold text-gray-800 whitespace-nowrap">
                  Social Networks :
                </span>
                <div className="flex gap-2">
                  {viewLead.socialLinks?.facebook && (
                    <div className="w-6 h-6 rounded-md flex items-center justify-center bg-purple-100 text-purple-600 cursor-pointer hover:scale-110 transition-transform">
                      <FacebookFilled />
                    </div>
                  )}
                  {viewLead.socialLinks?.twitter && (
                    <div className="w-6 h-6 rounded-md flex items-center justify-center bg-sky-100 text-sky-600 cursor-pointer hover:scale-110">
                      <TwitterOutlined />
                    </div>
                  )}
                  {viewLead.socialLinks?.instagram && (
                    <div className="w-6 h-6 rounded-md flex items-center justify-center bg-orange-100 text-orange-600 cursor-pointer hover:scale-110 transition-transform">
                      <InstagramOutlined />
                    </div>
                  )}
                  {viewLead.socialLinks?.github && (
                    <div className="w-6 h-6 rounded-md flex items-center justify-center bg-green-100 text-green-700 cursor-pointer hover:scale-110 transition-transform">
                      <GithubOutlined />
                    </div>
                  )}
                  {viewLead.socialLinks?.youtube && (
                    <div className="w-6 h-6 rounded-md flex items-center justify-center bg-red-100 text-red-500 cursor-pointer hover:scale-110 transition-transform">
                      <YoutubeOutlined />
                    </div>
                  )}
                </div>
              </div>
              <hr
                className="border-t border-dotted p-3 "
                style={{ borderColor: "gray-200" }}
              />

              <div className="px-4 pb-4">
                <p className="font-semibold text-[#001529] mb-2">Tags:</p>
                <div className="flex flex-wrap gap-2">
                  {viewLead.tags?.map((tagId, i) => {
                    const tagInfo = tagLookup[tagId];
                    const tagName = tagInfo?.name || tagId;

                    return (
                      <span
                        key={i}
                        className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full font-semibold"
                        style={{
                          fontSize: "10px",
                          padding: "4px 10px",
                          whiteSpace: "nowrap",
                          textAlign: "center",
                          borderRadius: "4px",
                          fontWeight: 700,
                          display: "inline-block",
                        }}
                      >
                        {tagName}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </Drawer>

      {/* Edit Modal */}
      {currentLead && (
        <Editcontactmodal
          visible={isEditModalVisible}
          customerId={currentLead._id}
          onCancel={() => setIsEditModalVisible(false)}
          onUpdate={handleUpdate}
          initialValues={currentLead}
          onCreate={handleCreate}
          refreshContacts={() => fetchContacts({ page: 1, search: searchText })}
          sources={sources}
          tags={tags}
        />
      )}

      {/* Add Modal */}
      <Addcontactmodal
        visible={isAddModalVisible}
        onCancel={() => setIsAddModalVisible(false)}
        onCreate={handleCreate}
        refreshContacts={() => fetchContacts({ page: 1, search: searchText })}
      />
    </>
  );
};

export const CONTACTS_TABLE_COLUMNS = [
  "contactName",
  "score",
  "email",
  "phone",
  "company",
  "leadSource",
  "tags",
];

export default ContactTable;
